import React, { useState, useEffect, useCallback } from 'react';
import { formatInTenantTz } from '../../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Progress } from '../../ui/progress';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useAuth } from '../../../contexts/AuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../hooks/use-toast';
import { 
  Bot, 
  Brain, 
  Zap, 
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  MessageSquare,
  BarChart3,
  Target,
  Lightbulb,
  RefreshCw,
  Settings,
  BookOpen,
  ThumbsUp,
  Star,
  Upload,
  Download
} from 'lucide-react';

const AIAgentDashboard = () => {
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  // D3 — use the canonical Firebase-authenticated request helper (fresh ID token + backend base
  // URL + 401 refresh). Replaces the prior non-Firebase sessionStorage 'token' + relative fetch.
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [activeConversations, setActiveConversations] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [configureOpen, setConfigureOpen] = useState(false);
  const [trainAIOpen, setTrainAIOpen] = useState(false);
  const [addKnowledgeOpen, setAddKnowledgeOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [viewAnalyticsOpen, setViewAnalyticsOpen] = useState(false);
  const [trainingLogsOpen, setTrainingLogsOpen] = useState(false);
  const [reviewCasesOpen, setReviewCasesOpen] = useState(false);
  const [caseDetailsOpen, setCaseDetailsOpen] = useState(false);
  const [provideFeedbackOpen, setProvideFeedbackOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  
  // Form states
  const [aiConfig, setAiConfig] = useState({
    confidenceThreshold: '0.85',
    maxResponseTime: '3',
    autoEscalation: true,
    learningMode: 'active',
    supportedLanguages: ['en', 'es', 'fr']
  });
  
  const [newKnowledge, setNewKnowledge] = useState({
    title: '',
    content: '',
    category: 'technical',
    tags: '',
    priority: 'medium'
  });
  
  const [trainingData, setTrainingData] = useState({
    scenario: '',
    expectedResponse: '',
    difficulty: 'medium',
    category: 'general'
  });

  // Fetch AI metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!currentBusiness?.id) return;
      
      setLoading(true);
      try {
        const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/metrics/${currentBusiness.brandId}`);
        if (response.ok) {
          const data = await response.json();
          setMetrics(data);
        }
      } catch (error) {
        console.error('Error fetching AI metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, [currentBusiness?.id]);

  // Fetch active conversations (component-scope so the Refresh button can re-invoke it)
  const fetchActiveConversations = useCallback(async () => {
    if (!currentBusiness?.id) return;

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/conversations/business/${currentBusiness.brandId}`);
      if (response.ok) {
        const data = await response.json();
        const aiProcessing = data.conversations?.filter(c =>
          c.status === 'ai_processing' || c.ai_agent_id
        ) || [];
        setActiveConversations(aiProcessing);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [currentBusiness?.id, currentBusiness?.brandId, makeAuthenticatedRequest]);

  useEffect(() => {
    fetchActiveConversations();
    const interval = setInterval(fetchActiveConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchActiveConversations]);

  // Fetch knowledge base
  useEffect(() => {
    const fetchKnowledgeBase = async () => {
      if (!currentBusiness?.id) return;
      
      try {
        const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/knowledge-base/${currentBusiness.brandId}`);
        if (response.ok) {
          const data = await response.json();
          setKnowledgeBase(data.knowledge_base || []);
        }
      } catch (error) {
        console.error('Error fetching knowledge base:', error);
      }
    };

    fetchKnowledgeBase();
  }, [currentBusiness?.id]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ai_resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'ai_processing':
        return <Brain className="h-4 w-4 text-blue-500 animate-pulse" />;
      case 'escalated':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ai_resolved':
        return 'bg-green-100 text-green-800';
      case 'ai_processing':
        return 'bg-blue-100 text-blue-800';
      case 'escalated':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Handler functions for buttons
  const handleConfigure = async () => {
    if (!currentBusiness?.brandId) return;
    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/config/${currentBusiness.brandId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(aiConfig)
      });
      if (response.ok) {
        toast({
          title: t('aa.aiConfigUpdated'),
          description: t('aa.aiConfigSaved'),
        });
        setConfigureOpen(false);
      } else {
        throw new Error(`Failed to save configuration (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Error saving AI configuration:', error);
      toast({
        title: t('common.error'),
        description: t('aa.saveConfigError'),
        variant: "destructive",
      });
    }
  };

  const handleTrainAI = () => {
    if (!trainingData.scenario || !trainingData.expectedResponse) {
      toast({
        title: t('aa.missingInfo'),
        description: t('aa.trainingFillScenario'),
        variant: "destructive",
      });
      return;
    }

    // No AI model-training endpoint exists yet — do not fake a completed training run.
    // real wiring lands in the AI backend phase
    toast({
      title: t('aa.notAvailableYet'),
      description: t('aa.trainingUnavailable'),
    });

    setTrainingData({
      scenario: '',
      expectedResponse: '',
      difficulty: 'medium',
      category: 'general'
    });
    setTrainAIOpen(false);
  };

  const handleAddKnowledge = async () => {
    if (!newKnowledge.title || !newKnowledge.content) {
      toast({
        title: t('aa.missingInfo'),
        description: t('aa.fillTitleContent'),
        variant: "destructive",
      });
      return;
    }

    // Real persistence via the existing POST /ai-agent/knowledge-base endpoint.
    const entry = {
      title: newKnowledge.title,
      content: newKnowledge.content,
      category: newKnowledge.category,
      priority: newKnowledge.priority,
      business_id: currentBusiness?.brandId || 'default',
      tags: newKnowledge.tags.split(',').map(t => t.trim()).filter(t => t),
      success_rate: 0,
      usage_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/knowledge-base`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry)
      });

      if (response.ok) {
        setKnowledgeBase(prev => [...prev, entry]);
        toast({
          title: t('aa.knowledgeAdded'),
          description: t('aa.knowledgeAddedDesc'),
        });
        setNewKnowledge({
          title: '',
          content: '',
          category: 'technical',
          tags: '',
          priority: 'medium'
        });
        setAddKnowledgeOpen(false);
      } else {
        throw new Error(`Failed to add entry (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Error adding knowledge entry:', error);
      toast({
        title: t('common.error'),
        description: t('aa.addKnowledgeError'),
        variant: "destructive",
      });
    }
  };

  const handleImportKnowledge = async () => {
    // No knowledge-base import endpoint exists yet — do not fake a successful import.
    // real wiring lands in the AI backend phase
    toast({
      title: t('aa.notAvailableYet'),
      description: t('aa.importUnavailable'),
    });
    setImportOpen(false);
  };

  const handleExportKnowledge = async (format) => {
    // Real client-side export of the knowledge base already loaded into state (no backend needed).
    if (!knowledgeBase || knowledgeBase.length === 0) {
      toast({
        title: t('aa.nothingToExport'),
        description: t('aa.nothingToExportDesc'),
      });
      return;
    }

    let blob;
    let filename;
    if (format === 'csv') {
      const headers = ['id', 'title', 'category', 'content', 'usage_count', 'success_rate'];
      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = knowledgeBase.map(e => headers.map(h => escape(e[h])).join(','));
      blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv' });
      filename = `knowledge-base-${currentBusiness?.brandId || 'export'}.csv`;
    } else {
      blob = new Blob([JSON.stringify(knowledgeBase, null, 2)], { type: 'application/json' });
      filename = `knowledge-base-${currentBusiness?.brandId || 'export'}.json`;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: t('aa.exportComplete'),
      description: t('aa.exportCompleteDesc', { format: format.toUpperCase() }),
    });
    setExportOpen(false);
  };

  const handleUpdateTrainings = async () => {
    // No training-update endpoint exists yet — honest placeholder rather than a fake progress toast.
    // real wiring lands in the AI backend phase
    toast({
      title: t('aa.notAvailableYet'),
      description: t('aa.trainingUpdatesUnavailable'),
    });
  };

  const handleReviewCases = () => {
    setReviewCasesOpen(true);
  };

  const handleViewAnalytics = () => {
    toast({
      title: t('aa.loadingAnalytics'),
      description: t('aa.loadingAnalyticsDesc'),
    });
    setViewAnalyticsOpen(true);
  };

  const handleViewTrainingLogs = () => {
    toast({
      title: t('aa.loadingTrainingLogs'),
      description: t('aa.loadingTrainingLogsDesc'),
    });
    setTrainingLogsOpen(true);
  };

  const handleViewCaseDetails = (caseItem) => {
    setSelectedCase(caseItem);
    setCaseDetailsOpen(true);
    toast({
      title: t('aa.loadingCaseDetails'),
      description: t('aa.loadingCaseDetailsDesc', { id: caseItem.id }),
    });
  };

  const handleProvideFeedback = (caseItem) => {
    setSelectedCase(caseItem);
    setFeedbackText('');
    setProvideFeedbackOpen(true);
  };

  const handleSubmitFeedback = () => {
    if (!feedbackText.trim()) {
      toast({
        title: t('aa.feedbackRequired'),
        description: t('aa.feedbackRequiredDesc'),
        variant: "destructive",
      });
      return;
    }

    toast({
      title: t('aa.feedbackSubmitted'),
      description: t('aa.feedbackSubmittedDesc', { id: selectedCase?.id }),
    });

    setFeedbackText('');
    setProvideFeedbackOpen(false);
    setSelectedCase(null);
  };

  // No backend endpoint exposes escalated cases for review yet; keep an honest empty source.
  // real wiring lands in the AI backend phase
  const escalatedCases = [];

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-blue-50 to-purple-50">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
              <Bot className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('aa.dashboardTitle')}</h1>
              <p className="text-gray-600">
                {t('aa.dashboardSubtitle')}
                {user?.name && <span className="text-gray-400 ml-1">{t('aa.managedBy', { name: user.name })}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Badge className="bg-green-100 text-green-800">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
              {t('aa.active')}
            </Badge>
            <Dialog open={configureOpen} onOpenChange={setConfigureOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Settings className="h-4 w-4 mr-2" />
                  {t('aa.configure')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('aa.configTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('aa.configDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">{t('aa.confidenceThreshold')}</label>
                    <Select value={aiConfig.confidenceThreshold} onValueChange={(value) => setAiConfig(prev => ({ ...prev, confidenceThreshold: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0.70">{t('aa.confThreshold70')}</SelectItem>
                        <SelectItem value="0.80">{t('aa.confThreshold80')}</SelectItem>
                        <SelectItem value="0.85">{t('aa.confThreshold85')}</SelectItem>
                        <SelectItem value="0.90">{t('aa.confThreshold90')}</SelectItem>
                        <SelectItem value="0.95">{t('aa.confThreshold95')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('aa.maxResponseTime')}</label>
                    <Input
                      type="number"
                      value={aiConfig.maxResponseTime}
                      onChange={(e) => setAiConfig(prev => ({ ...prev, maxResponseTime: e.target.value }))}
                      min="1"
                      max="10"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('aa.learningMode')}</label>
                    <Select value={aiConfig.learningMode} onValueChange={(value) => setAiConfig(prev => ({ ...prev, learningMode: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">{t('aa.activeLearning')}</SelectItem>
                        <SelectItem value="passive">{t('aa.passiveLearning')}</SelectItem>
                        <SelectItem value="supervised">{t('aa.supervisedOnly')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('aa.features')}</label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={aiConfig.autoEscalation}
                        onChange={(e) => setAiConfig(prev => ({ ...prev, autoEscalation: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-sm">{t('aa.autoEscalation')}</span>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setConfigureOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleConfigure}>
                      {t('aa.saveConfiguration')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={trainAIOpen} onOpenChange={setTrainAIOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                  <Brain className="h-4 w-4 mr-2" />
                  {t('aa.trainAI')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('aa.trainingSessionTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('aa.trainingSessionDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">{t('aa.customerScenario')}</label>
                    <Textarea
                      placeholder={t('aa.scenarioPlaceholder')}
                      value={trainingData.scenario}
                      onChange={(e) => setTrainingData(prev => ({ ...prev, scenario: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('aa.expectedResponse')}</label>
                    <Textarea
                      placeholder={t('aa.expectedResponsePlaceholder')}
                      value={trainingData.expectedResponse}
                      onChange={(e) => setTrainingData(prev => ({ ...prev, expectedResponse: e.target.value }))}
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">{t('aa.difficultyLevel')}</label>
                      <Select value={trainingData.difficulty} onValueChange={(value) => setTrainingData(prev => ({ ...prev, difficulty: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="easy">{t('aa.easy')}</SelectItem>
                          <SelectItem value="medium">{t('aa.medium')}</SelectItem>
                          <SelectItem value="hard">{t('aa.hard')}</SelectItem>
                          <SelectItem value="expert">{t('aa.expert')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">{t('aa.category')}</label>
                      <Select value={trainingData.category} onValueChange={(value) => setTrainingData(prev => ({ ...prev, category: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">{t('aa.generalSupport')}</SelectItem>
                          <SelectItem value="technical">{t('aa.technicalIssues')}</SelectItem>
                          <SelectItem value="billing">{t('aa.billingQuestions')}</SelectItem>
                          <SelectItem value="product">{t('aa.productInformation')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setTrainAIOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleTrainAI}>
                      <Brain className="h-4 w-4 mr-2" />
                      {t('aa.trainAIModel')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Real-time Performance Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('aa.currentLoad')}</p>
                  <p className="text-2xl font-bold text-blue-600">{activeConversations.length}</p>
                  <p className="text-xs text-gray-500">{t('aa.activeAiConversations')}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-100">
                  <Brain className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('aa.avgResponse')}</p>
                  <p className="text-2xl font-bold text-green-600">{metrics.average_response_time != null ? `${metrics.average_response_time}s` : '—'}</p>
                  <p className="text-xs text-gray-500">{t('aa.responseTime')}</p>
                </div>
                <div className="p-3 rounded-full bg-green-100">
                  <Zap className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600 flex items-center gap-1">
                    <TrendingUp className="h-3.5 w-3.5" />
                    {t('aa.confidenceScore')}
                  </p>
                  <p className="text-2xl font-bold text-purple-600">{metrics.average_confidence_score != null ? `${(metrics.average_confidence_score * 100).toFixed(1)}%` : '—'}</p>
                  <p className="text-xs text-gray-500">{t('aa.avgConfidence')}</p>
                </div>
                <div className="p-3 rounded-full bg-purple-100">
                  <Target className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('aa.queueLength')}</p>
                  <p className="text-2xl font-bold text-orange-600">—</p>
                  <p className="text-xs text-gray-500">{t('aa.notAvailableYetLower')}</p>
                </div>
                <div className="p-3 rounded-full bg-orange-100">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="conversations" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="conversations">{t('aa.tab.conversations')}</TabsTrigger>
          <TabsTrigger value="analytics">{t('aa.tab.analytics')}</TabsTrigger>
          <TabsTrigger value="knowledge">{t('aa.tab.knowledge')}</TabsTrigger>
          <TabsTrigger value="learning">{t('aa.tab.learning')}</TabsTrigger>
        </TabsList>

        {/* Active Conversations Tab */}
        <TabsContent value="conversations" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Conversations List */}
            <div className="lg:col-span-2">
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{t('aa.realtimeConversations', { count: activeConversations.length })}</span>
                    <Button variant="ghost" size="sm" onClick={fetchActiveConversations} title={t('aa.refreshConversations')}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                        <p className="text-sm text-gray-500">{t('aa.loadingConversations')}</p>
                      </div>
                    ) : activeConversations.length === 0 ? (
                      <div className="text-center py-8">
                        <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">{t('aa.noActiveConversations')}</p>
                        <p className="text-sm text-gray-400">{t('aa.aiHandlesAutomatically')}</p>
                      </div>
                    ) : (
                      activeConversations.map((conversation) => (
                        <div
                          key={conversation.id}
                          className="p-4 rounded-lg border bg-white hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-500 text-white font-semibold">
                                  {conversation.customer_name?.charAt(0) || 'C'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <h4 className="font-medium text-gray-900">{t('aa.customerInquiry')}</h4>
                                <p className="text-sm text-gray-600">{conversation.category}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(conversation.status)}
                              <Badge className={getStatusColor(conversation.status)}>
                                {conversation.status?.replace('_', ' ')}
                              </Badge>
                            </div>
                          </div>
                          
                          <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                            {conversation.messages?.[0]?.message || t('aa.processing')}
                          </p>
                          
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-500">
                              {conversation.created_at ? formatInTenantTz(conversation.created_at, { timeStyle: 'medium' }) : t('aa.justNow')}
                            </div>
                            <div className="flex space-x-2">
                              {/* real wiring lands in the AI backend phase */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toast({
                                  title: t('aa.notAvailableYet'),
                                  description: t('aa.convDetailUnavailable'),
                                })}
                              >
                                {t('aa.viewDetails')}
                              </Button>
                              {conversation.status === 'ai_processing' && (
                                <Button
                                  size="sm"
                                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
                                  onClick={() => toast({
                                    title: t('aa.notAvailableYet'),
                                    description: t('aa.escalationUnavailable'),
                                  })}
                                >
                                  {t('aa.escalate')}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* AI Status Panel */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-lg text-blue-900">{t('aa.aiStatus')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* System resource telemetry (CPU/memory/processing speed) is not exposed by any
                      backend endpoint yet — show an honest placeholder instead of fabricated gauges. */}
                  {/* real wiring lands in the AI backend phase */}
                  <div className="text-sm text-blue-900/70">
                    {t('aa.telemetryUnavailable')}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{t('aa.quickActions')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700" onClick={() => setAddKnowledgeOpen(true)}>
                    <Lightbulb className="h-4 w-4 mr-2" />
                    {t('aa.addKnowledge')}
                  </Button>

                  <Dialog open={viewAnalyticsOpen} onOpenChange={setViewAnalyticsOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full" onClick={handleViewAnalytics}>
                        <BarChart3 className="h-4 w-4 mr-2" />
                        {t('aa.viewAnalytics')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{t('aa.analyticsTitle')}</DialogTitle>
                        <DialogDescription>
                          {t('aa.analyticsDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      {/* Values derived from the real GET /ai-agent/metrics response (honest 0/— when absent). */}
                      <div className="space-y-6">
                        <div className="grid grid-cols-4 gap-4">
                          <Card>
                            <CardContent className="p-4 text-center">
                              <div className="text-2xl font-bold text-green-600">{metrics.auto_resolution_rate != null ? `${metrics.auto_resolution_rate.toFixed(1)}%` : '—'}</div>
                              <div className="text-sm text-gray-600">{t('aa.autoResolutionRate')}</div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="p-4 text-center">
                              <div className="text-2xl font-bold text-blue-600">{metrics.average_response_time != null ? `${metrics.average_response_time}s` : '—'}</div>
                              <div className="text-sm text-gray-600">{t('aa.avgResponse')}</div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="p-4 text-center">
                              <div className="text-2xl font-bold text-purple-600">{metrics.conversations_handled != null ? metrics.conversations_handled : '—'}</div>
                              <div className="text-sm text-gray-600">{t('aa.conversations')}</div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="p-4 text-center">
                              <div className="text-2xl font-bold text-orange-600">{metrics.escalation_rate != null ? `${metrics.escalation_rate.toFixed(1)}%` : '—'}</div>
                              <div className="text-sm text-gray-600">{t('aa.escalationRate')}</div>
                            </CardContent>
                          </Card>
                        </div>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">{t('aa.performanceBreakdown')}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span>{t('aa.resolutionRate')}</span>
                                <div className="flex items-center space-x-2">
                                  <Progress value={metrics.auto_resolution_rate ?? 0} className="w-24" />
                                  <span className="text-sm font-medium">{metrics.auto_resolution_rate != null ? `${metrics.auto_resolution_rate.toFixed(0)}%` : '—'}</span>
                                </div>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5 text-gray-400" />{t('aa.customerSatisfaction')}</span>
                                <div className="flex items-center space-x-2">
                                  <Progress value={(metrics.customer_satisfaction_avg || 0) / 5 * 100} className="w-24" />
                                  <span className="text-sm font-medium">{metrics.customer_satisfaction_avg != null ? `${metrics.customer_satisfaction_avg.toFixed(1)}/5.0` : '—'}</span>
                                </div>
                              </div>
                              <div className="flex justify-between items-center">
                                <span>{t('aa.responseTimeLabel')}</span>
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium">{metrics.average_response_time != null ? `${metrics.average_response_time}s` : '—'}</span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={trainingLogsOpen} onOpenChange={setTrainingLogsOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full" onClick={handleViewTrainingLogs}>
                        <BookOpen className="h-4 w-4 mr-2" />
                        {t('aa.trainingLogs')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{t('aa.trainingLogsTitle')}</DialogTitle>
                        <DialogDescription>
                          {t('aa.trainingLogsDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      {/* No training-logs endpoint exists yet — honest empty state, not fabricated history. */}
                      {/* real wiring lands in the AI backend phase */}
                      <div className="text-center py-12">
                        <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">{t('aa.noTrainingLogs')}</p>
                        <p className="text-sm text-gray-400">{t('aa.trainingHistoryUpcoming')}</p>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Performance Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('aa.resolutionPerformance')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('aa.autoResolutionRate')}</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-green-600">{(metrics.auto_resolution_rate ?? 0).toFixed(1)}%</span>
                    </div>
                  </div>
                  <Progress value={metrics.auto_resolution_rate || 0} className="h-3" />

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('aa.escalationRate')}</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-orange-600">{(metrics.escalation_rate ?? 0).toFixed(1)}%</span>
                    </div>
                  </div>
                  <Progress value={metrics.escalation_rate || 0} className="h-3" />

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('aa.customerSatisfaction')}</span>
                    <div className="flex items-center space-x-2">
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      <span className="font-semibold">{metrics.customer_satisfaction_avg != null ? `${metrics.customer_satisfaction_avg.toFixed(1)}/5.0` : '—'}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('aa.learningProgress')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{knowledgeBase.length}</div>
                    <div className="text-sm text-gray-600">{t('aa.knowledgeBaseEntries')}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="text-lg font-semibold text-green-700">{metrics.successful_resolutions ?? 0}</div>
                      <div className="text-xs text-green-600">{t('aa.successfulResolutions')}</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="text-lg font-semibold text-blue-700">{metrics.conversations_handled ?? 0}</div>
                      <div className="text-xs text-blue-600">{t('aa.totalConversations')}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Knowledge Base Tab */}
        <TabsContent value="knowledge" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('aa.knowledgeBaseEntriesCount', { count: knowledgeBase.length })}</span>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" onClick={() => setImportOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    {t('aa.import')}
                  </Button>
                  <Button variant="outline" onClick={() => setExportOpen(true)}>
                    <Download className="h-4 w-4 mr-2" />
                    {t('aa.export')}
                  </Button>
                  <Button 
                    className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAddKnowledgeOpen(true);
                    }}
                  >
                    <Lightbulb className="h-4 w-4 mr-2" />
                    {t('aa.addEntry')}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {knowledgeBase.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('aa.noKnowledgeEntries')}</h3>
                    <p className="text-gray-500 mb-6">{t('aa.addFirstEntry')}</p>
                    <Button 
                      className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAddKnowledgeOpen(true);
                      }}
                    >
                      <Lightbulb className="h-4 w-4 mr-2" />
                      {t('aa.createFirstEntry')}
                    </Button>
                  </div>
                ) : (
                  knowledgeBase.slice(0, 10).map((entry) => (
                    <div key={entry.id} className="p-4 border rounded-lg bg-white hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{entry.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{entry.category}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline" className="text-xs">
                            {t('aa.uses', { count: entry.usage_count || 0 })}
                          </Badge>
                          <div className="flex items-center space-x-1">
                            <Star className="h-3 w-3 text-yellow-500 fill-current" />
                            <span className="text-xs">{entry.success_rate != null ? `${entry.success_rate.toFixed(1)}%` : '—'}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2">{entry.content}</p>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {entry.tags?.slice(0, 3).map((tag, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Learning & Feedback Tab */}
        <TabsContent value="learning" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('aa.recentFeedback')}</CardTitle>
              </CardHeader>
              <CardContent>
                {/* No customer-feedback list endpoint exists yet — honest empty state, not fabricated reviews. */}
                {/* real wiring lands in the AI backend phase */}
                <div className="text-center py-10">
                  <ThumbsUp className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{t('aa.noFeedback')}</p>
                  <p className="text-sm text-gray-400">{t('aa.feedbackWillAppear')}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('aa.learningOpportunities')}</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Automated learning-opportunity detection (pattern/anomaly analytics) is not
                    backed by an endpoint yet — copy is generic (no fabricated figures) and the
                    actions degrade honestly. real wiring lands in the AI backend phase */}
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-medium text-yellow-800 mb-2">{t('aa.improveResolutionRate')}</h4>
                    <p className="text-sm text-yellow-700 mb-3">
                      {t('aa.improveResolutionDesc')}
                    </p>
                    <Button
                      size="sm"
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                      onClick={handleReviewCases}
                    >
                      {t('aa.reviewCases')}
                    </Button>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">{t('aa.patternDetection')}</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      {t('aa.patternDetectionDesc')}
                    </p>
                    <div className="flex items-center justify-end">
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={handleUpdateTrainings}
                      >
                        {t('aa.updateTrainings')}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Knowledge Dialog */}
      <Dialog open={addKnowledgeOpen} onOpenChange={setAddKnowledgeOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('aa.addKnowledgeTitle')}</DialogTitle>
            <DialogDescription>
              {t('aa.addKnowledgeDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('aa.titleRequired')}</label>
              <Input
                placeholder={t('aa.knowledgeTitlePlaceholder')}
                value={newKnowledge.title}
                onChange={(e) => setNewKnowledge(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('aa.contentRequired')}</label>
              <Textarea
                placeholder={t('aa.contentPlaceholder')}
                rows={4}
                value={newKnowledge.content}
                onChange={(e) => setNewKnowledge(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('aa.category')}</label>
              <Select
                value={newKnowledge.category}
                onValueChange={(value) => setNewKnowledge(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technical">{t('aa.technical')}</SelectItem>
                  <SelectItem value="billing">{t('aa.billing')}</SelectItem>
                  <SelectItem value="general">{t('aa.general')}</SelectItem>
                  <SelectItem value="policy">{t('aa.policy')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t('aa.priority')}</label>
              <Select
                value={newKnowledge.priority}
                onValueChange={(value) => setNewKnowledge(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t('aa.low')}</SelectItem>
                  <SelectItem value="medium">{t('aa.medium')}</SelectItem>
                  <SelectItem value="high">{t('aa.high')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t('aa.tags')}</label>
              <Input
                placeholder={t('aa.tagsPlaceholder')}
                value={newKnowledge.tags}
                onChange={(e) => setNewKnowledge(prev => ({ ...prev, tags: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setAddKnowledgeOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleAddKnowledge}>
                {t('aa.addToKnowledgeBase')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('aa.importTitle')}</DialogTitle>
            <DialogDescription>
              {t('aa.importDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('aa.selectFile')}</label>
              <Input
                type="file"
                accept=".csv,.json"
                className="mt-2"
              />
            </div>
            <div className="text-xs text-gray-500">
              {t('aa.supportedFormats')}
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setImportOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleImportKnowledge}>
                {t('aa.import')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('aa.exportTitle')}</DialogTitle>
            <DialogDescription>
              {t('aa.exportDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-20 flex flex-col p-3"
                onClick={() => handleExportKnowledge('csv')}
              >
                <Download className="h-6 w-6 mb-1" />
                <span className="text-xs font-medium">{t('aa.csvFormat')}</span>
                <span className="text-xs text-gray-500">{t('aa.csvFormatDesc')}</span>
              </Button>
              <Button
                variant="outline"
                className="h-20 flex flex-col p-3"
                onClick={() => handleExportKnowledge('json')}
              >
                <Download className="h-6 w-6 mb-1" />
                <span className="text-xs font-medium">{t('aa.jsonFormat')}</span>
                <span className="text-xs text-gray-500">{t('aa.jsonFormatDesc')}</span>
              </Button>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setExportOpen(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Cases Dialog */}
      <Dialog open={reviewCasesOpen} onOpenChange={setReviewCasesOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t('aa.reviewEscalatedTitle')}</DialogTitle>
            <DialogDescription>
              {t('aa.reviewEscalatedDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">{t('aa.recentEscalatedCases')}</h3>
              </div>

              {/* No escalated-cases review endpoint exists yet — render from an (empty) real source
                  so this list is honest rather than showing fabricated ESC-001/002/003 records.
                  real wiring lands in the AI backend phase */}
              {escalatedCases.length === 0 && (
                <div className="text-center py-10">
                  <AlertTriangle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{t('aa.noEscalatedCases')}</p>
                  <p className="text-sm text-gray-400">{t('aa.escalatedWillAppear')}</p>
                </div>
              )}

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {escalatedCases.map((case_item) => (
                  <div key={case_item.id} className="p-4 border rounded-lg bg-white hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Badge variant="outline" className="text-xs">
                            {case_item.id}
                          </Badge>
                          <Badge className={
                            case_item.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                            case_item.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            'bg-blue-100 text-blue-800'
                          }>
                            {case_item.priority}
                          </Badge>
                          <span className="text-sm text-gray-500">{case_item.timestamp}</span>
                        </div>
                        <h4 className="font-medium text-gray-900 mb-1">{case_item.customer}</h4>
                        <p className="text-sm text-gray-600 mb-2">{case_item.issue}</p>
                        <p className="text-xs text-orange-600">
                          <AlertTriangle className="h-3 w-3 inline mr-1" />
                          {case_item.escalationReason}
                        </p>
                      </div>
                      <div className="text-right space-y-2">
                        <div className="text-sm">
                          <span className="text-gray-500">{t('aa.aiConfidenceLabel')}</span>
                          <span className={`ml-1 font-medium ${
                            parseInt(case_item.aiConfidence) < 50 ? 'text-red-600' : 'text-yellow-600'
                          }`}>
                            {case_item.aiConfidence}
                          </span>
                        </div>
                        <div className="space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewCaseDetails(case_item)}
                          >
                            {t('aa.viewDetails')}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleProvideFeedback(case_item)}
                          >
                            {t('aa.provideFeedback')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-medium mb-4">{t('aa.escalationAnalysis')}</h3>
              {/* real wiring lands in the AI backend phase */}
              <p className="text-sm text-gray-500">{t('aa.escalationAnalysisUnavailable')}</p>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setReviewCasesOpen(false)}>
                {t('common.close')}
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => toast({
                  title: t('aa.notAvailableYet'),
                  description: t('aa.analysisReportUnavailable'),
                })}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                {t('aa.generateAnalysisReport')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Case Details Dialog */}
      <Dialog open={caseDetailsOpen} onOpenChange={setCaseDetailsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('aa.caseDetailsTitle', { id: selectedCase?.id })}</DialogTitle>
            <DialogDescription>
              {t('aa.caseDetailsDesc')}
            </DialogDescription>
          </DialogHeader>
          {selectedCase && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('aa.caseId')}</label>
                  {/* D3: friendly case reference, never the raw case/database id */}
                  <p className="text-sm text-gray-900">{selectedCase.ticket_id || (selectedCase.id ? `CASE-${String(selectedCase.id).slice(-6).toUpperCase()}` : '—')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('aa.priority')}</label>
                  <Badge className={(selectedCase.priority === 'high') ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                    {(selectedCase.priority || 'medium').toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('aa.customer')}</label>
                  <p className="text-sm text-gray-900">{selectedCase.customer || t('aa.unknownCustomer')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('aa.escalated')}</label>
                  <p className="text-sm text-gray-900">{selectedCase.timestamp || t('aa.unknownTime')}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">{t('aa.issueDescription')}</label>
                <p className="text-sm text-gray-900 mt-1 p-3 bg-gray-50 rounded-lg">
                  {selectedCase.issue || t('aa.noIssueDescription')}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">{t('aa.aiConfidenceLevel')}</label>
                <div className="mt-1">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${selectedCase.confidence != null ? selectedCase.confidence : 0}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">{selectedCase.confidence != null ? `${selectedCase.confidence}%` : '—'}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">{t('aa.escalationReason')}</label>
                <p className="text-sm text-gray-600 mt-1">
                  {t('aa.escalationReasonText')}
                </p>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCaseDetailsOpen(false)}>
                  {t('common.close')}
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setCaseDetailsOpen(false);
                    handleProvideFeedback(selectedCase);
                  }}
                >
                  {t('aa.provideFeedback')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Provide Feedback Dialog */}
      <Dialog open={provideFeedbackOpen} onOpenChange={setProvideFeedbackOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('aa.provideFeedbackCaseTitle', { id: selectedCase?.id })}</DialogTitle>
            <DialogDescription>
              {t('aa.provideFeedbackDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">{t('aa.caseSummary')}</label>
              <div className="mt-1 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900">
                  {t('aa.feedbackCustomer', { name: selectedCase?.customer })}<br/>
                  {t('aa.feedbackIssue', { issue: selectedCase?.issue })}
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">{t('aa.yourFeedback')}</label>
              <Textarea
                placeholder={t('aa.feedbackPlaceholder')}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <div className="flex items-start space-x-2">
                <Lightbulb className="h-4 w-4 text-green-600 mt-0.5" />
                <div className="text-sm text-green-800">
                  <p className="font-medium">{t('aa.feedbackGuidelines')}</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>{t('aa.feedbackGuideline1')}</li>
                    <li>{t('aa.feedbackGuideline2')}</li>
                    <li>{t('aa.feedbackGuideline3')}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setProvideFeedbackOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSubmitFeedback}
                className="bg-green-600 hover:bg-green-700"
              >
                {t('aa.submitFeedback')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIAgentDashboard;